import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { Grid, Box, Typography } from '@mui/material';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import { typesRedux } from '../../../shared/types/typesRedux';
import { useAuthContext } from '../../../contexts/authContext';
import LoadingButtonGeneral from '../../components/general/buttons/loadingButtonGeneral';
import { ToastSwal } from '../../components/general/toastSwals/toastSwal';
import { HOME } from '../../../routes/paths';
import { msalInstance } from '../../../auth/msalConfig';
import { Client } from '@microsoft/microsoft-graph-client';
import 'isomorphic-fetch';
import { Copyright } from '../../components/general/copyright/copyright';
import { asyncGetMyUser } from '../../../services/users.service';
import { asyncSharepointGetAuthUrl } from '../../../services/sharepoint-graph.service';

export default function SignIn() {
    const dispatch: any = useDispatch();
    const { logout, tokenAuth, setTokenAuth } = useAuthContext();

    const [loadingMicrosoft, setLoadingMicrosoft] = useState(false);

    useEffect(() => {
        dispatch({ type: typesRedux.LOGOUT_LOGIN });
        logout();
    }, []);

    if (tokenAuth) return <Navigate to={HOME} />;

    const handleMicrosoftLogin = async () => {
        setLoadingMicrosoft(true);
        try {
            const loginResponse = await msalInstance.loginPopup({
                scopes: ['openid', 'profile', 'email', 'User.Read'],
            });

            const accessToken = loginResponse.accessToken;

            const client = Client.init({
                authProvider: (done) => {
                    done(null, accessToken);
                },
            });

            const graphUser = await client
                .api('/me')
                .select('displayName,jobTitle,mail,userPrincipalName')
                .get();

            const userPayload = {
                id: loginResponse.account.localAccountId,
                username: graphUser.mail || graphUser.userPrincipalName,
                name: graphUser.displayName,
                email: graphUser.mail || graphUser.userPrincipalName,
                type: 'Microsoft',
                jobTitle: graphUser.jobTitle,
            };

            setTokenAuth(loginResponse.idToken);
            localStorage.setItem('My_Token', loginResponse.idToken);
            const redirectUrl = window.location.origin + '/sharepoint';

            const response = await asyncSharepointGetAuthUrl(redirectUrl);
            if (response.authUrl) {
                window.location.href = response.authUrl;
            }

            if (userPayload.username) {
                const user = await asyncGetMyUser(userPayload.username);
                if (user) {
                    dispatch({
                        type: typesRedux.USER_LOG,
                        payload: user?.items,
                    });
                } else {
                    dispatch({
                        type: typesRedux.USER_LOG,
                        payload: userPayload,
                    });
                }
            }

            msalInstance.setActiveAccount(loginResponse.account);
        } catch (error) {
            ToastSwal.fire({
                text: 'Error al iniciar sesión con Microsoft',
                icon: 'error',
            });
            console.error('Microsoft login error:', error);
        } finally {
            setLoadingMicrosoft(false);
        }
    };

    return (
        <Grid
            container
            component="main"
            className="bg-galaxy"
            sx={{
                height: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    borderRadius: '50px',
                    paddingX: 5,
                    paddingY: 10,
                    maxWidth: 500,
                    width: '100%',
                    gap: 3,
                }}
            >
                <Typography
                    component="h1"
                    variant="h5"
                    sx={{ mt: 2, color: 'primary.main' }}
                >
                    ¡Bienvenido de nuevo!
                </Typography>
                <Box
                    sx={{
                        mt: 3,
                        mb: 3,
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Box
                        component="img"
                        src={require('../../../assets/images/logos/logo-victoria.png')}
                        alt="logo"
                        sx={{
                            width: '70%',
                            maxWidth: 800,
                            objectFit: 'contain',
                            filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))',
                        }}
                    />
                </Box>

                <Typography
                    component="h1"
                    variant="body2"
                    sx={{ color: 'text.secondary' }}
                >
                    Por favor, inicia sesión con tu cuenta
                </Typography>
                <LoadingButtonGeneral
                    onClick={handleMicrosoftLogin}
                    title="Iniciar sesión con Microsoft"
                    fullWidth
                    loading={loadingMicrosoft}
                    loadingPosition="end"
                    sx={{ backgroundColor: 'primary.main' }}
                    startIcon={<MicrosoftIcon />}
                    disabled={loadingMicrosoft}
                />
            </Box>
            <Copyright className="copyrigth_sign_in" />
        </Grid>
    );
}
